import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2,
  ChevronUp, ChevronDown, Ticket, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import CouponFormModal from '../../components/admin/CouponFormModal';
import DiscountFormModal from '../../components/admin/DiscountFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import SearchableSelect from '../../components/admin/SearchableSelect';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

interface Coupon {
  id: number;
  code: string;
  discount_type: string;
  discount_type_display: string;
  discount_value: number;
  min_purchase_amount: number;
  usage_limit: number | null;
  used_count: number;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  is_valid: boolean;
  scope: string;
  shop?: number | null;
  target_type: string;
  target_products?: {id:number;name:string}[];
  category?: number|null;
}

const CouponManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [scheduledDiscounts,setScheduledDiscounts]=useState<any[]>([]);
  const [discountModalOpen,setDiscountModalOpen]=useState(false);
  const [selectedDiscount,setSelectedDiscount]=useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });



  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCoupons();
      const data = response.data || response;
      const couponsList = Array.isArray(data) ? data : (data.results || data.data || []);
      setCoupons(couponsList);
      const discountResponse=await adminAPI.getDiscounts();
      setScheduledDiscounts(Array.isArray(discountResponse.data)?discountResponse.data:(discountResponse.data.results||[]));
    } catch (error) {
      console.error('Failed to load coupons:', error);
      setToast({ show: true, message: 'Failed to load coupons', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCoupon) return;
    
    try {
      await adminAPI.deleteCoupon(selectedCoupon.id);
      setToast({ show: true, message: 'Coupon deleted successfully', type: 'success' });
      loadCoupons();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete coupon', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedCoupon(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedCoupon) {
        await adminAPI.updateCoupon(selectedCoupon.id, formData);
        setToast({ show: true, message: 'Coupon updated successfully', type: 'success' });
      } else {
        await adminAPI.createCoupon(formData);
        setToast({ show: true, message: 'Coupon created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedCoupon(null);
      loadCoupons();
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedCoupon(null);
    setIsModalOpen(true);
  };
  const saveDiscount=async(data:any)=>{if(selectedDiscount)await adminAPI.updateDiscount(selectedDiscount.id,data);else await adminAPI.createDiscount(data);setToast({show:true,message:'Discount saved successfully',type:'success'});setSelectedDiscount(null);await loadCoupons()};
  const deleteDiscount=async(discount:any)=>{if(!window.confirm(`Delete discount ${discount.code}?`))return;await adminAPI.deleteDiscount(discount.id);setToast({show:true,message:'Discount deleted',type:'success'});loadCoupons()};
  const unassignDiscount=async(discount:any)=>{if(!window.confirm(`Remove ${discount.code} from every assigned product?`))return;await adminAPI.unassignDiscountProducts(discount.id);setToast({show:true,message:'Discount removed from all products',type:'success'});loadCoupons()};
  const unassignCoupon=async(coupon:Coupon)=>{if(!window.confirm(`Remove ${coupon.code} from all targeted products?`))return;await adminAPI.unassignCouponProducts(coupon.id);setToast({show:true,message:'Coupon removed from all products',type:'success'});loadCoupons()};

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const couponsArray = Array.isArray(coupons) ? coupons : [];
  const filtered = useMemo(() => {
    let list = couponsArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(c => c.code.toLowerCase().includes(q));
    }
    if (statusFilter === 'active') list = list.filter(c => c.is_active);
    else if (statusFilter === 'inactive') list = list.filter(c => !c.is_active);
    else if (statusFilter === 'valid') list = list.filter(c => c.is_valid);
    else if (statusFilter === 'expired') list = list.filter(c => !c.is_valid);
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [couponsArray, searchTerm, statusFilter, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const targetLabel=(rule:any)=>rule.target_type==='ALL_PRODUCTS'?'All products':rule.target_type==='CATEGORY'?(rule.category_name||`Category #${rule.category}`):rule.target_type==='ASSIGNED_PRODUCTS'?`${rule.assigned_products||0} inventory-assigned`:(rule.target_products||[]).map((x:any)=>x.name).join(', ')||'Selected products';

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Discounts & Coupons</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={()=>{setSelectedDiscount(null);setDiscountModalOpen(true)}}><Plus size={13}/> Add Discount</button>
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}><Plus size={13} /> Add Coupon <span className="adm-btn-badge">{coupons.length}</span></button>
          
        </div>
      </div>
      <div className="erp-card" style={{marginBottom:'14px', padding:'14px 16px'}}>
        <strong>One promotion engine:</strong> Configure flat or percentage rules here. Admin discounts are assigned to products by code from Inventory; customers enter coupon codes during checkout.
      </div>
      <div className="erp-card" style={{marginBottom:'14px'}}><div className="erp-table-title"><h2>Admin Discounts</h2><span>{scheduledDiscounts.length} rules</span></div><div className="erp-table-wrap"><table><thead><tr><th>Code</th><th>Type / Value</th><th>Sales Channel</th><th>Product Target</th><th>Valid Period</th><th>Status</th><th></th></tr></thead><tbody>{scheduledDiscounts.length?scheduledDiscounts.map((rule:any)=><tr key={rule.id}><td><strong>{rule.code}</strong></td><td>{rule.discount_type==='percentage'?`${rule.discount_value}%`:`৳${rule.discount_value}`}</td><td>{rule.scope==='SHOP'?(rule.shop_name||'Shop'):rule.scope.replaceAll('_',' ')}</td><td>{targetLabel(rule)}</td><td>{fmtDate(rule.valid_from)} → {fmtDate(rule.valid_to)}</td><td>{rule.is_valid?'Active now':rule.is_active?'Scheduled / expired':'Inactive'}</td><td><div className="adm-actions-row"><button className="adm-action-btn" title="Remove from all products" onClick={()=>unassignDiscount(rule)}><XCircle size={12}/></button><button className="adm-action-btn adm-ab-edit" onClick={()=>{setSelectedDiscount(rule);setDiscountModalOpen(true)}}><Pencil size={12}/></button><button className="adm-action-btn adm-ab-del" onClick={()=>deleteDiscount(rule)}><Trash2 size={12}/></button></div></td></tr>):<tr><td colSpan={7}>No admin discounts configured.</td></tr>}</tbody></table></div></div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <SearchableSelect value={statusFilter} onChange={next => { setStatusFilter(next); setCurrentPage(1); }} placeholder="All statuses" options={[{value:'active',label:'Active'},{value:'inactive',label:'Inactive'},{value:'valid',label:'Valid now'},{value:'expired',label:'Expired / unavailable'}]}/>
          <span className="adm-count"><strong>{filtered.length}</strong> coupons</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by code…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading coupons…</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No coupons found.</div>}
          {!loading && filtered.map(c => {
            const isPct = c.discount_type === 'percentage';
            return (
              <div className="mobile-card" key={c.id}>
                <div className="mc-top">
                  <div className="mc-thumb-placeholder"><Ticket size={22} /></div>
                  <div className="mc-info"><div className="mc-title">{c.code}</div><div className="mc-subtitle">{c.discount_type_display}</div></div>
                  <div className="mc-badge-corner"><span className={`mc-active-pill ${c.is_valid ? 'active' : 'inactive'}`}>{c.is_valid ? 'Valid' : 'Expired'}</span></div>
                </div>
                <div className="mc-divider" />
                <div className="mc-meta">
                  <div className="mc-meta-item"><span className="mc-meta-label">Discount:</span><span className="mc-meta-value" style={{ color: isPct ? '#7c3aed' : '#059669' }}>{isPct ? `${c.discount_value}%` : `৳${c.discount_value}`}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">Min:</span><span className="mc-meta-value">৳{c.min_purchase_amount}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">Used:</span><span className="mc-meta-value">{c.usage_limit ? `${c.used_count}/${c.usage_limit}` : c.used_count}</span></div>
                </div>
                <div className="mc-actions">
                  <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(c)}><Pencil size={12} /> Edit</button>
                  <button className="mc-btn" onClick={() => unassignCoupon(c)}><XCircle size={12}/> Unassign</button><button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(c)}><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('code')}>Code <SA field="code" /></th>
                  <th className="adm-th">Type</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('discount_value')}>Value <SA field="discount_value" /></th>
                  <th className="adm-th">Channel / Products</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('min_purchase_amount')}>Min Purchase <SA field="min_purchase_amount" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('used_count')}>Usage <SA field="used_count" /></th>
                  <th className="adm-th">Valid Period</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                  <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading coupons…</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={9} className="adm-empty">No coupons found.</td></tr>}
                {!loading && paginated.map(c => {
                  const isPct = c.discount_type === 'percentage';
                  const nearLimit = c.usage_limit ? c.used_count >= c.usage_limit * 0.8 : false;
                  return (
                    <tr className="adm-row" key={c.id}>
                      <td className="adm-td"><span className="adm-mono">{c.code}</span></td>
                      <td className="adm-td"><span className={`adm-chip ${isPct ? 'adm-chip-purple' : 'adm-chip-amber'}`}>{c.discount_type_display}</span></td>
                      <td className="adm-td"><span style={{ color: isPct ? '#7c3aed' : '#059669', fontWeight: 800, fontSize: 14 }}>{isPct ? `${c.discount_value}%` : `৳${c.discount_value}`}</span></td>
                      <td className="adm-td"><small>{String(c.scope).replaceAll('_',' ')}<br/>{targetLabel(c)}</small></td>
                      <td className="adm-td"><span className="adm-price-main" style={{ fontSize: 12 }}>৳{c.min_purchase_amount}</span></td>
                      <td className="adm-td"><span style={{ color: nearLimit ? '#f59e0b' : '#64748b', fontWeight: 600 }}>{c.usage_limit ? `${c.used_count}/${c.usage_limit}` : c.used_count}</span></td>
                      <td className="adm-td"><span className="adm-date">{fmtDate(c.valid_from)} → {fmtDate(c.valid_to)}</span></td>
                      <td className="adm-td">
                        <span className={`adm-chip ${c.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>
                          {c.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}
                        </span>
                      </td>
                      <td className="adm-td" style={{ padding: '6px 4px' }}>
                        <div className="adm-actions-row">
                          <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(c)} title="Edit"><Pencil size={12} /></button>
                          <button className="adm-action-btn" onClick={()=>unassignCoupon(c)} title="Remove from all products"><XCircle size={12}/></button><button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(c)} title="Delete"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 coupons' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
            </div>
          </div>
        </div>
      )}

      <CouponFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedCoupon(null); 
        }}
        onSave={handleSave}
        coupon={selectedCoupon}
      />
      <DiscountFormModal isOpen={discountModalOpen} onClose={()=>{setDiscountModalOpen(false);setSelectedDiscount(null)}} onSave={saveDiscount} discount={selectedDiscount}/>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCoupon(null);
        }}
        onConfirm={handleDelete}
        title="Delete Coupon"
        message={`Are you sure you want to delete coupon "${selectedCoupon?.code}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default CouponManagement;
